import * as React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import type { OobMethod } from "@/types/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/billing-format";
import { useInvoiceOps } from "./use-billing-ops";

const METHODS: OobMethod[] = ["bank_transfer", "cash", "other"];

/** Today as YYYY-MM-DD in the operator's local time zone. */
function localToday(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface Props {
  /** Stripe invoice id (in_...). */
  stripeInvoiceId: string;
  instanceName: string | null;
  /** Human label for the dialog title: invoice number or id. */
  label: string;
  lastReminderAt: string | null | undefined;
}

/**
 * «Mark paid» + «Remind» + «Void» for one open invoice. Rendered only for
 * open invoices; the backend re-checks the live Stripe status anyway.
 */
export function InvoiceRowActions({ stripeInvoiceId, instanceName, label, lastReminderAt }: Props) {
  const { t } = useTranslation("billing");
  const { markPaid, remind, voidInvoice } = useInvoiceOps(stripeInvoiceId, instanceName);
  const [open, setOpen] = React.useState(false);
  const [voidOpen, setVoidOpen] = React.useState(false);

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          {t("ops.markPaid.button")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => remind.mutate()}
          disabled={remind.isPending}
          aria-label={t("ops.remind.aria", { invoice: label })}
        >
          {remind.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("ops.remind.button")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={() => setVoidOpen(true)}
          aria-label={t("ops.void.aria", { invoice: label })}
        >
          {t("ops.void.button")}
        </Button>
      </div>
      {lastReminderAt ? (
        <span className="text-xs text-muted-foreground">
          {t("ops.remind.last", { date: formatDate(lastReminderAt) })}
        </span>
      ) : null}
      <MarkPaidDialog
        open={open}
        onOpenChange={setOpen}
        label={label}
        pending={markPaid.isPending}
        onSubmit={(body) => markPaid.mutate(body, { onSuccess: () => setOpen(false) })}
      />
      <Dialog open={voidOpen} onOpenChange={(v) => !voidInvoice.isPending && setVoidOpen(v)}>
        <DialogContent closeLabel={t("ops.close")}>
          <DialogHeader>
            <DialogTitle>{t("ops.void.title", { invoice: label })}</DialogTitle>
            <DialogDescription>{t("ops.void.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setVoidOpen(false)}
              disabled={voidInvoice.isPending}
            >
              {t("ops.cancelAction")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={voidInvoice.isPending}
              onClick={() => voidInvoice.mutate(undefined, { onSuccess: () => setVoidOpen(false) })}
            >
              {voidInvoice.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("ops.void.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MarkPaidDialog({
  open,
  onOpenChange,
  label,
  pending,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  pending: boolean;
  onSubmit: (body: { method: OobMethod; paidOn: string; reference?: string; note?: string }) => void;
}) {
  const { t } = useTranslation("billing");
  const [method, setMethod] = React.useState<OobMethod>("bank_transfer");
  const [paidOn, setPaidOn] = React.useState(localToday);
  const [reference, setReference] = React.useState("");
  const [note, setNote] = React.useState("");
  const idPrefix = React.useId();

  React.useEffect(() => {
    if (open) {
      setMethod("bank_transfer");
      setPaidOn(localToday());
      setReference("");
      setNote("");
    }
  }, [open]);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(paidOn) && paidOn <= localToday();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || pending) return;
    onSubmit({
      method,
      paidOn,
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !pending && onOpenChange(v)}>
      <DialogContent closeLabel={t("ops.close")}>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t("ops.markPaid.title", { invoice: label })}</DialogTitle>
            <DialogDescription>{t("ops.markPaid.description")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-method`}>{t("ops.markPaid.method")}</Label>
              <Select value={method} onValueChange={(v) => setMethod(v as OobMethod)}>
                <SelectTrigger id={`${idPrefix}-method`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {t(`ops.markPaid.methods.${m}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-paid-on`}>{t("ops.markPaid.paidOn")}</Label>
              <Input
                id={`${idPrefix}-paid-on`}
                type="date"
                value={paidOn}
                max={localToday()}
                onChange={(e) => setPaidOn(e.target.value)}
                aria-invalid={!valid}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-reference`}>{t("ops.markPaid.reference")}</Label>
            <Input
              id={`${idPrefix}-reference`}
              value={reference}
              maxLength={200}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t("ops.markPaid.referencePlaceholder")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-note`}>{t("ops.markPaid.note")}</Label>
            <Textarea
              id={`${idPrefix}-note`}
              value={note}
              maxLength={500}
              rows={3}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              {t("ops.cancelAction")}
            </Button>
            <Button type="submit" disabled={!valid || pending}>
              {pending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("ops.markPaid.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
